import { Link } from "react-router-dom";
import { ArrowRight, Building2, MapPin, ShieldCheck } from "lucide-react";
import { useContentMeta, formatUkDate } from "./ContentDates";

export const FOUNDER = {
  name: "Ross",
  role: "Founder of Stag Vault",
  company: "Homeguard Developments Ltd",
  location: "Scotland-based",
  photo: "/founder.jpg",
  profiles: [
    { label: "Stag Vault on Instagram", href: "https://www.instagram.com/stagvault/" },
    { label: "Stag Vault on TikTok", href: "https://www.tiktok.com/@stagvault" },
    { label: "Homeguard Developments on Instagram", href: "https://www.instagram.com/homeguarddevelopments/" },
  ],
  paragraphs: [
    "Stag Vault was created by Ross, the Scotland-based founder behind Homeguard Developments Ltd. His work spans property development, digital products and the practical use of AI to turn early-stage ideas into structured commercial offers.",
    "Stag Vault grew from a simple frustration: AI can produce almost anything, but most people still need a clear route showing what to build, which prompt to run next and how to turn the output into something useful.",
    "Every Stag Vault track is designed around that principle—choose one commercial outcome, follow an ordered workflow and finish with an asset you can test in the real world.",
  ],
};

export function FounderBlock() {
  const meta = useContentMeta("/founder");
  return (
    <section className="border-b border-white/10 bg-[#08070a]" data-testid="founder-block">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 md:px-10 lg:grid-cols-12 lg:items-center lg:py-24">
        <div className="lg:col-span-4">
          <div className="aspect-[4/3] overflow-hidden border border-zinc-800 bg-zinc-900">
            <img src={FOUNDER.photo} alt="Ross, Scotland-based founder of Stag Vault and Homeguard Developments Ltd" width="726" height="690" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
          </div>
          <div className="mt-4 text-sm text-zinc-300"><span className="font-semibold text-white">{FOUNDER.name}</span> · {FOUNDER.role}</div>
        </div>
        <div className="lg:col-span-8 lg:pl-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-purple-300">Built by a person, not a content farm</div>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-black leading-[1.06] text-white sm:text-4xl">Built by Ross, a Founder Who Actually Builds Things</h2>
          {FOUNDER.paragraphs.map((p) => <p key={p.slice(0, 24)} className="mt-5 max-w-3xl text-base leading-relaxed text-zinc-300">{p}</p>)}
          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-amber-300" aria-hidden="true" /> {FOUNDER.location} founder</span>
            <span className="inline-flex items-center gap-2"><Building2 size={14} className="text-amber-300" aria-hidden="true" /> {FOUNDER.company} · UK registered</span>
            {meta && <span className="inline-flex items-center gap-2" data-testid="founder-last-reviewed"><ShieldCheck size={14} className="text-amber-300" aria-hidden="true" /> Last reviewed by Ross · {formatUkDate(meta.last_reviewed_at)}</span>}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <Link to="/founder" className="inline-flex min-h-11 items-center gap-2 border border-zinc-700 px-4 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors hover:border-purple-400" data-testid="founder-cta">Read the complete founder story <ArrowRight size={14} aria-hidden="true" /></Link>
            <Link to="/editorial-method" className="inline-flex min-h-11 items-center text-sm text-zinc-400 transition-colors hover:text-white" data-testid="founder-method-link">How tracks are researched and tested</Link>
            <Link to="/contact" className="inline-flex min-h-11 items-center text-sm text-zinc-500 transition-colors hover:text-white" data-testid="founder-support-link">Ask a question before buying</Link>
          </div>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs">
            {FOUNDER.profiles.map((p) => <a key={p.href} href={p.href} target="_blank" rel="noreferrer me" className="text-zinc-500 underline decoration-zinc-700 underline-offset-4 hover:text-purple-300">{p.label}</a>)}
          </div>
        </div>
      </div>
    </section>
  );
}
